import React, { createContext, useContext } from "react";
import { createProxiedClient, WrappedApiClient } from "./proxiedClient";
import {
  AddressApi,
  AdminEntitiesApi,
  AgentToolsApi,
  AiAgentsApi,
  AppointmentApi,
  CalendarApi,
  ClinicalDocumentationApi,
  DoctorApi,
  DocumentsApi,
  MasterlistsApi,
  NoteApi,
  PatientsApi,
  RxApi,
  SchedulerApi,
  TaxationApi,
  TicketsApi,
  TransactionsApi,
  UserProfileApi,
  WorkingHoursApi,
  PublicHolidayApi,
  SepaApi,
  KostentraegerApi,
} from "../api-client";
import { createApiConfig } from "./createApiConfig";
import { MaintenanceApi } from "./maintenanceApi";
// Types are provided by proxiedClient wrapper now

type Api = {
  calendar: WrappedApiClient<CalendarApi>;
  patients: WrappedApiClient<PatientsApi>;
  appointments: WrappedApiClient<AppointmentApi>;
  scheduler: WrappedApiClient<SchedulerApi>;
  masterlists: WrappedApiClient<MasterlistsApi>;
  rx: WrappedApiClient<RxApi>;
  transaction: WrappedApiClient<TransactionsApi>;
  taxation: WrappedApiClient<TaxationApi>;
  tickets: WrappedApiClient<TicketsApi>;
  address: WrappedApiClient<AddressApi>;
  user: WrappedApiClient<UserProfileApi>;
  doctors: WrappedApiClient<DoctorApi>;
  workingHours: WrappedApiClient<WorkingHoursApi>;
  documents: WrappedApiClient<DocumentsApi>;
  clinicalDocumentation: WrappedApiClient<ClinicalDocumentationApi>;
  notes: WrappedApiClient<NoteApi>;
  adminEntities: WrappedApiClient<AdminEntitiesApi>;
  agentTools: WrappedApiClient<AgentToolsApi>;
  aiAgents: WrappedApiClient<AiAgentsApi>;
  publicHoliday: WrappedApiClient<PublicHolidayApi>;
  maintenance: WrappedApiClient<MaintenanceApi>;
  sepa: WrappedApiClient<SepaApi>;
  kostentraeger: WrappedApiClient<KostentraegerApi>;
  // ...add modules                                                                             ************** ADD HERE **************
};

const ApiCtx = createContext<Api | null>(null);
ApiCtx.displayName = "ApiContext";

type Props = { children: React.ReactNode };

export const ApiProvider: React.FC<Props> = ({ children }) => {
  // Token is read fresh per-request via the function-based accessToken in createApiConfig
  const config = createApiConfig();

  // Create each module client once; requests always read fresh token via config getter
  const api: Api = {
    calendar: createProxiedClient(new CalendarApi(config)),
    patients: createProxiedClient(new PatientsApi(config)),
    appointments: createProxiedClient(new AppointmentApi(config)),
    scheduler: createProxiedClient(new SchedulerApi(config)),
    masterlists: createProxiedClient(new MasterlistsApi(config)),
    rx: createProxiedClient(new RxApi(config)),
    transaction: createProxiedClient(new TransactionsApi(config)),
    taxation: createProxiedClient(new TaxationApi(config)),
    tickets: createProxiedClient(new TicketsApi(config)),
    address: createProxiedClient(new AddressApi(config)),
    user: createProxiedClient(new UserProfileApi(config)),
    doctors: createProxiedClient(new DoctorApi(config)),
    workingHours: createProxiedClient(new WorkingHoursApi(config)),
    documents: createProxiedClient(new DocumentsApi(config)),
    clinicalDocumentation: createProxiedClient(new ClinicalDocumentationApi(config)),
    notes: createProxiedClient(new NoteApi(config)),
    adminEntities: createProxiedClient(new AdminEntitiesApi(config)),
    agentTools: createProxiedClient(new AgentToolsApi(config)),
    aiAgents: createProxiedClient(new AiAgentsApi(config)),
    publicHoliday: createProxiedClient(new PublicHolidayApi(config)),
    maintenance: createProxiedClient(new MaintenanceApi(config)),
    sepa: createProxiedClient(new SepaApi(config)),
    kostentraeger: createProxiedClient(new KostentraegerApi(config)),
    // ...add modules here                                                                            ************** ADD HERE **************
  };

  return <ApiCtx.Provider value={api}>{children}</ApiCtx.Provider>;
};

export const useApi = (): Api => {
  const ctx = useContext(ApiCtx);
  if (!ctx) throw new Error("useApi must be used within ApiProvider");
  return ctx;
};
